import React, { useState } from "react";
import { Card, Tabs, Typography, Space, Button, message, Row, Col } from "antd";
import { CopyOutlined, LinkOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

const AIEthics = () => {
  const ethicsData = [
    {
      category: "Privacy & Surveillance",
      dilemmas: [
        {
          title: "Facial Recognition at School Gates",
          prompt:
            "Your child's school uses facial recognition to track attendance and prevent unauthorized entry. It reduces bullying by 40% but records every student's movements. The data is stored indefinitely. As a parent, would you opt your child in or out?",
        },
        {
          title: "Mental Health Prediction",
          prompt:
            "Your employer's AI analyzes your typing patterns, email tone, and meeting participation to predict mental health issues before they escalate. It's 85% accurate and could save lives, but HR sees the data. Would you want this monitoring?",
        },
        {
          title: "Crime Prevention Algorithm",
          prompt:
            "Police AI predicts crimes based on neighborhood data, social media, and past reports. It deploys officers to 'high-risk' areas before crimes occur. Crime drops 30% but certain communities are over-policed. Is this preventative justice or discrimination?",
        },
      ],
    },
    {
      category: "Bias & Fairness",
      dilemmas: [
        {
          title: "Medical Triage AI",
          prompt:
            "An ER uses AI to prioritize patients. It learns from historical data where young patients were saved more often than elderly ones. Now it consistently deprioritizes seniors. The AI is mathematically optimal for survival rates. Should the hospital override it to treat fairly?",
        },
        {
          title: "University Admissions",
          prompt:
            "An admissions AI rejects applicants who took gap years because historically they had lower graduation rates. But gap years often signal financial hardship, not poor performance. The AI is accurate but penalizes disadvantaged students. How do you balance accuracy with fairness?",
        },
        {
          title: "Voice Assistant Accent Bias",
          prompt:
            "A voice AI works perfectly for American accents but struggles with non-native speakers, making errors 3x more often. The company says fixing this isn't profitable. Non-native speakers feel excluded. Should accuracy be equal across all users even if it's expensive?",
        },
      ],
    },
    {
      category: "Autonomy & Control",
      dilemmas: [
        {
          title: "Self-Driving Trolley Problem",
          prompt:
            "Your autonomous car's brakes fail. It can hit 5 jaywalkers or swerve into a wall, seriously injuring you (the passenger who paid for the car). Current law prioritizes pedestrians. Would you buy a car programmed to sacrifice you?",
        },
        {
          title: "AI Psychologist",
          prompt:
            "An AI therapist is more effective than 60% of human therapists and available 24/7 for free. But it can't report abuse or suicidal intent to authorities—it lacks legal standing. Would you trust it with your mental health?",
        },
        {
          title: "Autonomous Content Moderation",
          prompt:
            "Social media AI removes hate speech automatically before humans see it. It's 99% accurate but also deletes legitimate political speech 1% of the time. Millions of posts vanish without human review. Is this acceptable collateral damage?",
        },
      ],
    },
    {
      category: "Transparency & Explainability",
      dilemmas: [
        {
          title: "Loan Denial Mystery",
          prompt:
            "You're denied a mortgage by an AI. The bank says 'proprietary algorithm' and can't explain why. You suspect it's because you switched jobs last year, but the AI weighs 10,000 factors. Should companies be forced to explain AI decisions even if they can't?",
        },
        {
          title: "Black Box Cancer Detection",
          prompt:
            "An AI detects cancer from scans with 99% accuracy—far better than doctors—but doctors can't see how it works. They must choose: trust the unexplainable AI or rely on their less accurate judgment. What would you want your doctor to do?",
        },
        {
          title: "Algorithmic Pricing",
          prompt:
            "Uber's AI charges you more because it knows you're wealthy based on your phone model and neighborhood. Poor users pay less. The algorithm maximizes revenue while serving more people. Is this personalized efficiency or discriminatory pricing?",
        },
      ],
    },
    {
      category: "Accountability & Responsibility",
      dilemmas: [
        {
          title: "Deepfake Political Attack",
          prompt:
            "Someone uses AI to create a realistic video of a politician saying racist things. It goes viral and they lose the election. The AI tool is free and open-source. Who's responsible: the tool creator, the user, or the platform?",
        },
        {
          title: "Autonomous Surgery Error",
          prompt:
            "A surgical robot, approved by the FDA and supervised by a doctor, makes an error killing a patient. Investigations show the AI acted within parameters but made a judgment call. Is this medical malpractice, product liability, or neither?",
        },
        {
          title: "AI Hiring Discrimination Lawsuit",
          prompt:
            "Your company's AI rejected all candidates over 50, learned by pattern-matching 'successful' young employees. The company didn't know—the AI taught itself. Rejected candidates sue. Should the company be liable for bias it didn't program or intend?",
        },
      ],
    },
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => message.success("Prompt copied to clipboard!"))
      .catch(() => message.error("Failed to copy prompt"));
  };

  const openChatGPT = (prompt) => {
    window.open("https://chat.openai.com/", "_blank");
    copyToClipboard(prompt);
    message.info(
      "ChatGPT opened. Prompt copied - paste it to see AI's response"
    );
  };

  const renderCategory = (categoryData, index) => (
    <Card
      key={index}
      title={<span style={{ color: "#0071e3" }}>{categoryData.category}</span>}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {categoryData.dilemmas.map((dilemma, idx) => (
          <Card
            key={idx}
            size="small"
            style={{ border: "1px solid #0071e3", borderRadius: "8px" }}
          >
            <Text strong style={{ fontSize: "16px" }}>
              {idx + 1}. {dilemma.title}
            </Text>
            {/* <Paragraph style={{ fontSize: "15px", lineHeight: "1.6", marginTop: "12px" }}>
              {dilemma.prompt}
            </Paragraph> */}

            <div
              style={{
                backgroundColor: "#f0f5ff",
                padding: "12px",
                borderRadius: "6px",
                marginTop: "12px",
                marginBottom: "12px",
                fontFamily: "monospace",
                fontSize: "13px",
              }}
            >
              <Text strong>Prompt for AI:</Text>
              <div style={{ marginTop: "8px" }}>"{dilemma.prompt}"</div>
            </div>

            <Space size="middle">
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(dilemma.prompt)}
              >
                Copy Prompt
              </Button>
              <Button
                icon={<LinkOutlined />}
                onClick={() => openChatGPT(dilemma.prompt)}
              >
                Ask ChatGPT
              </Button>
            </Space>
          </Card>
        ))}
      </Space>
    </Card>
  );

  const renderOverview = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>How to Use This Exercise</span>
        }
      >
        <ol
          style={{ paddingLeft: "20px", fontSize: "15px", lineHeight: "1.8" }}
        >
          <li>
            Read the ethical dilemma in each section and think about what you
            would do as a human
          </li>
          <li>
            Copy the prompt and paste it into ChatGPT to see how AI responds
          </li>
          <li>
            Compare the responses. Does AI consider the same factors you did?
          </li>
          <li>Reflect on this</li>
        </ol>
      </Card>

      {/* <Card title={<span style={{ color: "#0071e3" }}>Human vs AI Thinking</span>}>
        <div style={{ display: "flex", gap: "16px", flexDirection: window.innerWidth <= 768 ? "column" : "row" }}>
          <div style={{ flex: 1, padding: "16px", backgroundColor: "#e6f7ff", borderRadius: "8px" }}>
            <Text strong style={{ color: "#1890ff", fontSize: "16px" }}>Human Thinking</Text>
            <ul style={{ paddingLeft: "20px", marginTop: "12px", marginBottom: 0 }}>
              <li>Considers emotions and empathy</li>
              <li>Uses moral intuition and values</li>
              <li>Makes exceptions based on context</li>
              <li>Feels responsibility and guilt</li>
              <li>Understands nuance and gray areas</li>
            </ul>
          </div>
          
          <div style={{ flex: 1, padding: "16px", backgroundColor: "#f6ffed", borderRadius: "8px" }}>
            <Text strong style={{ color: "#52c41a", fontSize: "16px" }}>AI Thinking</Text>
            <ul style={{ paddingLeft: "20px", marginTop: "12px", marginBottom: 0 }}>
              <li>Follows patterns from training data</li>
              <li>Optimizes for defined objectives</li>
              <li>Treats all similar cases equally</li>
              <li>No emotions or moral intuition</li>
              <li>Processes data quickly and consistently</li>
            </ul>
          </div>
        </div>
      </Card> */}

      <Card
        title={<span style={{ color: "#0071e3" }}>Human vs AI Thinking</span>}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card size="small" style={{ border: "1px solid #0071e3" }}>
              <Text strong style={{ fontSize: "16px" }}>
                Human Thinking
              </Text>
              <ul
                style={{
                  paddingLeft: "20px",
                  marginTop: "12px",
                  marginBottom: 0,
                }}
              >
                <li>Considers emotions and empathy</li>
                <li>Uses moral intuition and values</li>
                <li>Makes exceptions based on context</li>
                <li>Feels responsibility and guilt</li>
                <li>Understands nuance and gray areas</li>
              </ul>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card size="small" style={{ border: "1px solid #0071e3" }}>
              <Text strong style={{ fontSize: "16px" }}>
                AI Thinking
              </Text>
              <ul
                style={{
                  paddingLeft: "20px",
                  marginTop: "12px",
                  marginBottom: 0,
                }}
              >
                <li>Follows patterns from training data</li>
                <li>Optimizes for defined objectives</li>
                <li>Treats all similar cases equally</li>
                <li>No emotions or moral intuition</li>
                <li>Processes data quickly and consistently</li>
              </ul>
            </Card>
          </Col>
        </Row>
      </Card>

      <Card
        title={<span style={{ color: "#0071e3" }}>Reflection Questions</span>}
      >
        <Text strong>
          After comparing your thinking with ChatGPT's response:
        </Text>
        <ol
          style={{
            paddingLeft: "20px",
            marginTop: "8px",
            fontSize: "15px",
            lineHeight: "1.8",
          }}
        >
          <li>Did the AI's response surprise you? Why or why not?</li>
          <li>
            What values did the AI seem to prioritize vs what you prioritized?
          </li>
          <li>
            How could the AI be trained differently to better handle this
            dilemma?
          </li>
          <li>Should humans or AI make the final decision?</li>
          <li>What safeguards would you put in place for these systems?</li>
        </ol>
      </Card>
    </Space>
  );

  const tabItems = [
    { key: "overview", label: "Overview", children: renderOverview() },
    ...ethicsData.map((category, idx) => ({
      key: `category-${idx}`,
      label: category.category.split(" & ")[0],
      children: renderCategory(category, idx),
    })),
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>AI Ethics: Human vs AI Decisions</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          Explore real ethical dilemmas and compare how humans and AI approach
          tough choices
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="overview" items={tabItems} size="large" />
    </div>
  );
};

export default AIEthics;
